// Pure date/grouping helpers for the Notes journal/calendar page. Kept free of
// React and the API client so the month-grid math and note-anchoring rules can
// be unit-tested in isolation. Dates cross the wire as ISO 8601 UTC strings
// (CLAUDE.md Sec.7); the calendar groups on the UTC date so a note's day never
// drifts with the viewer's time zone, matching the trade calendar's UTC keying.

export interface MonthCursor {
  year: number;
  month: number; // 0-based, matching Date#getUTCMonth
}

export const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const WEEKDAY_MINI_HEADERS = [
  "S",
  "M",
  "T",
  "W",
  "T",
  "F",
  "S",
] as const;

const WEEK_ROWS = 6;
const DAYS_PER_WEEK = 7;
const MS_PER_DAY = 86_400_000;

export function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

export function monthCursorFromNow(now: Date): MonthCursor {
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
}

export function shiftMonth(cursor: MonthCursor, delta: number): MonthCursor {
  const total = cursor.year * 12 + cursor.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function monthLabel(cursor: MonthCursor): string {
  return `${MONTH_LABELS[cursor.month]} ${cursor.year}`;
}

export function todayIso(now: Date): string {
  return `${now.getUTCFullYear().toString().padStart(4, "0")}-${pad2(now.getUTCMonth() + 1)}-${pad2(now.getUTCDate())}`;
}

// UTC [since, until] bounds for the cursor's month, as ISO strings — the shape
// the analytics/notes endpoints expect. Those endpoints treat the range as a
// CLOSED interval (until is inclusive: `closed_at <= until`), so the upper bound
// is the last representable instant of the month: the next month's start minus
// one millisecond (JS's finest resolution). The former inclusive `23:59:59`
// dropped the final ~1s of the month — a close at 23:59:59.5 fell outside every
// month — while this half-open-by-shift bound closes that gap without ever
// overlapping the next month's `since` (its 00:00:00.000 start).
export function monthBounds(cursor: MonthCursor): { since: string; until: string } {
  const since = new Date(Date.UTC(cursor.year, cursor.month, 1, 0, 0, 0));
  const until = new Date(Date.UTC(cursor.year, cursor.month + 1, 1, 0, 0, 0) - 1);
  return { since: since.toISOString(), until: until.toISOString() };
}

// UTC [since, until] bounds for the cursor's whole year, same closed-interval
// convention as monthBounds (see there). Feeds the calendar's annual mini-grid
// totals.
export function yearBounds(cursor: MonthCursor): { since: string; until: string } {
  const since = new Date(Date.UTC(cursor.year, 0, 1, 0, 0, 0));
  const until = new Date(Date.UTC(cursor.year + 1, 0, 1, 0, 0, 0) - 1);
  return { since: since.toISOString(), until: until.toISOString() };
}

// "YYYY-MM" key for an ISO date or timestamp — used to test month membership.
export function isoMonthKey(iso: string): string {
  return iso.slice(0, 7);
}

export function monthCursorKey(cursor: MonthCursor): string {
  return `${cursor.year.toString().padStart(4, "0")}-${pad2(cursor.month + 1)}`;
}

// The minimal note shape the journal anchors on — keeps this module decoupled
// from the generated schema types.
export interface AnchorableNote {
  note_type: string;
  trading_day: string | null;
  created_at: string;
}

// The calendar day a note belongs to: its trading_day when it is a trading-day
// note, otherwise the UTC date of its creation timestamp.
export function noteAnchorDate(note: AnchorableNote): string {
  if (note.note_type === "trading_day" && note.trading_day) {
    return note.trading_day;
  }
  return note.created_at.slice(0, 10);
}

export interface DayGroup<T> {
  date: string; // YYYY-MM-DD
  items: T[];
}

// Group items by their anchor date, newest day first. Items within a day keep
// the order they arrived in (the API already sorts the page).
export function groupByAnchorDate<T>(
  items: ReadonlyArray<T>,
  anchorOf: (item: T) => string,
): DayGroup<T>[] {
  const byDate = new Map<string, T[]>();
  for (const item of items) {
    const date = anchorOf(item);
    const bucket = byDate.get(date);
    if (bucket === undefined) byDate.set(date, [item]);
    else bucket.push(item);
  }
  return Array.from(byDate.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .map(([date, dayItems]) => ({ date, items: dayItems }));
}

// Day groups over the UNION of the days that carry a note and the days the user
// traded (ADR 0036 D7): the journal lists every trading day, not only days that
// already have a note, so each trade day surfaces an "add note" affordance even
// when empty. A trade day with no note yields a group with `items: []`. Newest
// day first, matching groupByAnchorDate.
export function buildJournalDays<T>(
  items: ReadonlyArray<T>,
  anchorOf: (item: T) => string,
  tradeDays: ReadonlyArray<string>,
): DayGroup<T>[] {
  const byDate = new Map<string, T[]>();
  for (const item of items) {
    const date = anchorOf(item);
    const bucket = byDate.get(date);
    if (bucket === undefined) byDate.set(date, [item]);
    else bucket.push(item);
  }
  const allDates = new Set<string>(byDate.keys());
  for (const date of tradeDays) allDates.add(date);
  return Array.from(allDates)
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
    .map((date) => ({ date, items: byDate.get(date) ?? [] }));
}

export interface MiniGridCell {
  isoDate: string;
  day: number;
  inMonth: boolean;
}

// A gapless 6x7 grid for the cursor's month; leading/trailing cells carry the
// adjacent months' days (flagged inMonth=false so they render faded).
export function buildMiniGrid(cursor: MonthCursor): MiniGridCell[] {
  const firstDayOfWeek = new Date(Date.UTC(cursor.year, cursor.month, 1)).getUTCDay();
  const start = Date.UTC(cursor.year, cursor.month, 1 - firstDayOfWeek);
  return Array.from({ length: WEEK_ROWS * DAYS_PER_WEEK }, (_, i) => {
    const d = new Date(start + i * MS_PER_DAY);
    return {
      isoDate: `${d.getUTCFullYear().toString().padStart(4, "0")}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === cursor.month,
    };
  });
}

// Weekday label (uppercase, e.g. "WED") for an ISO date — the timeline day head.
const WEEKDAY_LONG = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;

function isoParts(isoDate: string): { y: number; m: number; d: number } {
  const parts = isoDate.split("-");
  return { y: Number(parts[0]), m: Number(parts[1]), d: Number(parts[2]) };
}

export function weekdayLabel(isoDate: string): string {
  const { y, m, d } = isoParts(isoDate);
  return WEEKDAY_LONG[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? "";
}

// "Wed, May 21" style serif day header from an ISO date.
export function dayHeaderLabel(isoDate: string): string {
  const { y, m, d } = isoParts(isoDate);
  return `${MONTH_LABELS[m - 1] ?? ""} ${d}, ${y}`;
}

// First non-empty markdown line of a body, stripped of leading heading/list
// markers — used as a note's title. Returns "" when the body is blank.
export function deriveTitle(body: string): string {
  for (const raw of body.split("\n")) {
    const line = raw.trim();
    if (line === "") continue;
    return line.replace(/^#{1,6}\s+/, "").replace(/^[-*]\s+/, "");
  }
  return "";
}

// The remaining body after the title line, collapsed to a single spaced string
// for the card snippet. Returns "" when there is nothing past the title.
export function deriveSnippet(body: string): string {
  const lines = body.split("\n");
  let titleTaken = false;
  const rest: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!titleTaken) {
      if (line === "") continue;
      titleTaken = true;
      continue;
    }
    if (line !== "") rest.push(line.replace(/^#{1,6}\s+/, "").replace(/^[-*]\s+/, ""));
  }
  return rest.join(" ");
}
